import React, { ComponentProps as CP } from 'react'
import classNames from 'classnames'
import { injectIntl, WrappedComponentProps } from 'react-intl'

import CircleInfoIcon from 'components/CircleInfoIcon'
import T from 'components/FormattedMessage'
import SlideUp from 'components/SlideUp'
import Button from 'components/Button'

import { WithWidth } from '@material-ui/core/withWidth'
import { createStyles, withStyles, WithStyles, Theme } from '@material-ui/core/styles'
import { Dialog, DialogContent, withMobileDialog, IconButton, Typography } from '@material-ui/core'
import { TypographyProps } from '@material-ui/core/Typography'

import lightBackgroundUrl from './assets/light_bg.png'
import darkBackgroundUrl from './assets/dark_bg.png'
import closerUrl from 'assets/images/closer.svg'

type Props = Omit<CP<typeof Dialog>, 'onClose'> & {
  variant: 'light' | 'dark'
  icon: React.ReactNode
  message: React.ReactNode
  headline: React.ReactNode
  actionLabel: React.ReactNode
  displayFAQ?: boolean
  displayNew?: boolean
  onActionClick?: () => void
  onClose?: () => void
  disableAction?: boolean
  bottomChild?: React.ReactNode
}

const styles = (theme: Theme) => createStyles({
  content: {
    backgroundSize: 'cover',
    paddingBottom: theme.spacing() * 8,
  },
  contentLight: {
    backgroundImage: `url(${lightBackgroundUrl})`,
    '& $message, & $faqContainer': {
      color: theme.palette.grey[700],
    },
  },
  contentDark: {
    backgroundImage: `url(${darkBackgroundUrl})`,
    '& $headline': {
      color: theme.palette.accent.contrastText,
    },
    '& $message, & $faqContainer': {
      color: theme.palette.secondary.light,
    },
  },
  headline: {
    marginBottom: theme.spacing(),
  },
  message: {
    marginBottom: theme.spacing() * 8,
  },
  closerContainer: {
    display: 'flex',
    marginRight: -theme.spacing() * 2,
    marginTop: -theme.spacing() * 2,
  },
  closerPadding: {
    height: 53.41,
    flex: 1,
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(),
  },
  faqContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  faqIcon: {
    marginRight: theme.spacing(),
  },
  newContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing() * 4,
    marginBottom: theme.spacing(),
  },
  new: {
    backgroundColor: '#1f2a3e',
    color: theme.palette.accent.contrastText,
    borderRadius: 3,
    padding: '3px 10px',
  },
  bottomContainer: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    left: 0,
  },
})

type InjectedProps = Props & WithWidth & WithStyles<typeof styles> & WrappedComponentProps

const HowToDialog: React.SFC<InjectedProps> = ({
  classes,
  icon,
  intl,
  variant,
  headline,
  message,
  onActionClick,
  displayFAQ,
  displayNew,
  actionLabel,
  bottomChild,
  disableAction,
  ...props
}) => {
  const contentClassName = {
    light: classes.contentLight,
    dark: classes.contentDark,
  }

  const messageTypographyVariant = {
    light: 'body2',
    dark: 'body1',
  }

  return (
    <Dialog
      maxWidth="sm"
      TransitionComponent={SlideUp}
      {...props}
    >
      <DialogContent
        style={{ width: props.fullScreen ? undefined : 400 }}
        className={classNames(classes.content, contentClassName[variant])}
      >
        <div className={classes.closerContainer}>
          <div className={classes.closerPadding} />

          {props.onClose && (
            <IconButton onClick={props.onClose}>
              <img alt="" src={closerUrl} />
            </IconButton>
          )}
        </div>
        <div className={classes.iconContainer}>
          {icon}
        </div>
        {displayNew && (
          <div className={classes.newContainer}>
            <Typography className={classes.new} align="center" variant="caption">
              <T id="common_text_new" />
            </Typography>
          </div>
        )}
        <Typography className={classes.headline} align="center" variant="h5">
          {headline}
        </Typography>
        <Typography
          className={classes.message}
          variant={messageTypographyVariant[variant] as TypographyProps['variant']}
        >
          {message}
        </Typography>
        {displayFAQ && (
          <a className={classes.faqContainer} href={intl.formatMessage({ id: 'userprofile_faq_url' })}>
            <CircleInfoIcon className={classes.faqIcon} />
            <Typography color="inherit" align="center" variant="body1">
              <T id="help_faq" />
            </Typography>
          </a>
        )}
        <div className={classes.bottomContainer}>
          {bottomChild}
          <Button
            fullWidth
            size="large"
            rounded={false}
            color="primary"
            variant="contained"
            disabled={disableAction}
            onClick={onActionClick || props.onClose}
          >
            {actionLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

const HowToDialogIntl = injectIntl(HowToDialog)
// TODO: Refact whole component...
// tslint:disable-next-line: no-any
const HowToDialogIntlStyle = withStyles(styles)(HowToDialogIntl) as any

export default withMobileDialog<Props>({ breakpoint: 'xs' })(HowToDialogIntlStyle)
